import React from "react";
import styles from "./PrivacyPolicy.module.scss";
import logoBlackSquare from "../assets/footer/cork-square-logo.png";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  const currentYear = new Date().getFullYear();

  return (
    <div className={styles.PrivacyPolicy}>
      <div className={styles.grid}>
        <div className={styles.logoContainer}>
          <Link to="/">
            <img src={logoBlackSquare} />
          </Link>
        </div>
        <div className={styles.mainColumn}>
          <h1>Privacy Policy</h1>

          <p>
            This Privacy Policy describes how Cork Matters ("we", "us", or
            "our") collects, uses, and shares your personal information when you
            visit our website or make a purchase from us. We are committed to
            protecting your privacy and complying with the General Data
            Protection Regulation (GDPR) and other applicable laws in Portugal
            and the EU. This policy should be read in conjunction with our Terms
            and Conditions and Cookies Policy.
          </p>

          <p>
            Cork Matters is the data controller for your personal data. Our
            contact details are: [insert full address], Portugal. Email:{" "}
            <a href="mailto:corkmatters.online@gmail.com">
              corkmatters.online@gmail.com
            </a>
            . Taxpayer number: [insert NIF]. Registration number: [insert
            registration number].
          </p>

          <h2>Personal Data We Collect</h2>
          <p>We collect the following types of personal data:</p>
          <ul>
            <li>
              <strong>Identity and Contact Data:</strong> Name, email address,
              phone number, billing and shipping address.
            </li>
            <li>
              <strong>Transaction Data:</strong> Details of purchases, including
              items ordered and payment method (but not full payment card
              details, as these are handled by Shopify).
            </li>
            <li>
              <strong>Technical Data:</strong> IP address, browser type, device
              information, and usage data collected via cookies and similar
              technologies (see our Cookies Policy).
            </li>
            <li>
              <strong>Marketing and Communications Data:</strong> Your
              preferences for receiving marketing from us and your communication
              preferences.
            </li>
            <li>
              <strong>Analytics Data:</strong> Interaction data collected via
              Firebase Analytics, such as button clicks, add-to-cart events, and
              purchase events.
            </li>
          </ul>
          <p>
            We do not collect sensitive personal data unless required for
            specific purposes, and only with your explicit consent.
          </p>

          <h2>How We Collect Your Data</h2>
          <p>We collect data directly from you when you:</p>
          <ul>
            <li>Create an account or place an order.</li>
            <li>Subscribe to our newsletter or contact us via forms.</li>
            <li>Interact with our site (e.g., adding items to cart).</li>
          </ul>
          <p>
            We also collect data automatically via cookies, Firebase Analytics,
            and Google Ads (see Cookies Policy for details). Payment data is
            collected and processed securely by Shopify during checkout.
          </p>

          <h2>Purposes and Legal Bases for Processing</h2>
          <p>We process your data for the following purposes:</p>
          <ul>
            <li>
              <strong>To fulfill your orders:</strong> Process purchases,
              deliver products, and provide customer support. Legal basis:
              Performance of a contract.
            </li>
            <li>
              <strong>To manage payments:</strong> We use Shopify for payment
              processing. Shopify handles your payment card details securely.
              Legal basis: Performance of a contract.
            </li>
            <li>
              <strong>To improve our website:</strong> Analyze usage via
              Firebase Analytics to enhance user experience. Legal basis:
              Legitimate interests (improving services).
            </li>
            <li>
              <strong>For marketing:</strong> Send promotional emails or display
              targeted ads via Google Ads, if you consent. Legal basis: Consent.
            </li>
            <li>
              <strong>To comply with legal obligations:</strong> Such as tax
              reporting or responding to authorities. Legal basis: Legal
              obligation.
            </li>
            <li>
              <strong>To prevent fraud:</strong> Monitor transactions for
              security. Legal basis: Legitimate interests (protecting our
              business).
            </li>
          </ul>

          <h2>Sharing Your Data</h2>
          <p>We share your data with third parties only when necessary:</p>
          <ul>
            <li>
              <strong>Shopify:</strong> For payment processing and order
              fulfillment. Shopify's privacy policy applies:{" "}
              <a
                href="https://www.shopify.com/legal/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.shopify.com/legal/privacy
              </a>
              .
            </li>
            <li>
              <strong>Google (Firebase and Ads):</strong> For analytics and
              advertising. See Google's Privacy Policy:{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://policies.google.com/privacy
              </a>
              .
            </li>
            <li>
              <strong>Shipping Providers:</strong> Such as [insert shipping
              company, e.g., CTT or DHL] to deliver your orders.
            </li>
            <li>
              <strong>Other Service Providers:</strong> IT support, email
              services, or legal advisors, all under GDPR-compliant agreements.
            </li>
            <li>
              <strong>Authorities:</strong> If required by law.
            </li>
          </ul>
          <p>
            We do not sell your data. International transfers (e.g., to Shopify
            in Canada) are protected by adequacy decisions or standard
            contractual clauses.
          </p>

          <h2>Data Retention</h2>
          <p>We retain your data only as long as necessary:</p>
          <ul>
            <li>Order data: Up to 10 years for tax and accounting purposes.</li>
            <li>
              Account data: Until you request deletion or after 2 years of
              inactivity.
            </li>
            <li>
              Analytics data: As per Google's retention policies (typically
              14-26 months).
            </li>
            <li>Marketing data: Until you withdraw consent.</li>
          </ul>
          <p>Data is securely deleted or anonymized when no longer needed.</p>

          <h2>Your Rights Under GDPR</h2>
          <p>You have the following rights regarding your personal data:</p>
          <ul>
            <li>
              <strong>Access:</strong> Request a copy of your data.
            </li>
            <li>
              <strong>Rectification:</strong> Correct inaccurate data.
            </li>
            <li>
              <strong>Erasure:</strong> Request deletion (subject to legal
              obligations).
            </li>
            <li>
              <strong>Restriction:</strong> Limit processing in certain cases.
            </li>
            <li>
              <strong>Portability:</strong> Receive your data in a transferable
              format.
            </li>
            <li>
              <strong>Objection:</strong> Object to processing based on
              legitimate interests or for marketing.
            </li>
            <li>
              <strong>Withdraw Consent:</strong> At any time, where processing
              relies on consent.
            </li>
          </ul>
          <p>
            To exercise these rights, contact us at{" "}
            <a href="mailto:corkmatters.online@gmail.com">
              corkmatters.online@gmail.com
            </a>
            . We will respond within one month. You can also complain to
            Portugal's CNPD (www.cnpd.pt) or your local data protection
            authority.
          </p>

          <h2>Data Security</h2>
          <p>
            We use appropriate technical and organizational measures to protect
            your data, including encryption, access controls, and secure
            servers. However, no method is 100% secure, so we cannot guarantee
            absolute security.
          </p>

          <h2>Children’s Privacy</h2>
          <p>
            Our services are not directed to children under 16. We do not
            knowingly collect data from children. If we become aware of such
            data, we will delete it.
          </p>

          <h2>Changes to This Privacy Policy</h2>
          <p>
            We may update this policy to reflect changes in our practices or
            legal requirements. We will notify you via email or a website notice
            for significant changes. Your continued use of the site after
            changes constitutes acceptance.
          </p>

          <h2>Contact Information</h2>
          <p>
            If you have questions about this Privacy Policy, please contact us
            at{" "}
            <a href="mailto:corkmatters.online@gmail.com">
              corkmatters.online@gmail.com
            </a>
            .
          </p>
          <p>Last updated: September 29, 2025</p>
        </div>
      </div>
    </div>
  );
}
